import { c as _c } from "react/compiler-runtime";
import { useField, useFormikContext } from 'formik';
import { useContext, useEffect, useMemo } from 'react';
import { Grid, GridUnit } from '@hookdeck/theme';
import { destination_http_methods } from '../../../../../../../../typings/Destination.interface';
import { source_allowed_http_methods } from '../../../../../../../../typings/Source.interface';
import { DestinationTypeFeatures, isSourceTypeFeatureUnmanaged, SourceAuthProvider, SourceType, SourceTypeFeatures, SourceTypeFeatureTypesUnmanaged, TypeFeatureFields } from '../../../../../../../../typings/Integration.interface';
import LINKS from '../../../../../configs/links';
import { fieldName } from '../../../../../utils';
import Alert from '../../../../common/base/Alert';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import CopyableField from '../../../../common/CopyableField';
import DropdownMenu from '../../../../common/DropdownMenu';
import CheckboxInput from '../../../../common/Form/Fields/CheckboxInput';
import EditorInput from '../../../../common/Form/Fields/EditorInput';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../../common/Form/Fields/SwitchInput';
import TextAreaInput from '../../../../common/Form/Fields/TextAreaInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { DashboardContext } from '../../DashboardContext';

/**
 * Checkbox order for the source HTTP method picker.
 *
 * Derived from the canonical list rather than hardcoded, so a method added there cannot
 * be missed here — that drift is what silently dropped `HEAD` from the form when it was
 * introduced. Anything without an explicit rank sorts to the end, which keeps the
 * familiar verbs first and puts the unusual ones (`HEAD`, `QUERY`) last.
 */
const method_display_rank: Record<string, number> = {
  GET: 0,
  POST: 1,
  PUT: 2,
  PATCH: 3,
  DELETE: 4
};
const source_method_display_order = [...source_allowed_http_methods].sort((a, b) => (method_display_rank[a] ?? 99) - (method_display_rank[b] ?? 99));
export function useFeatures(features, t0, t1, t2) {
  const $ = _c(9);
  const prefix = t0 === undefined ? "" : t0;
  const hide_optional_features = t1 === undefined ? false : t1;
  const in_guided_connection = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== features || $[1] !== hide_optional_features || $[2] !== in_guided_connection || $[3] !== prefix) {
    const default_features = {
      required_features: [] as (DestinationFeatureType | SourceFeatureType)[],
      advanced_features: [] as (DestinationFeatureType | SourceFeatureType)[]
    };
    let t4;
    if ($[5] !== hide_optional_features || $[6] !== in_guided_connection || $[7] !== prefix) {
      t4 = (acc, t5) => {
        const [type, config] = t5;
        if (config.managed) {
          return acc;
        }
        const feature = {
          ...config,
          fields: config.fields?.map(field => ({
            ...field,
            name: fieldName(field.name, prefix)
          })),
          type: type as keyof SourceTypeFeatures | DestinationTypeFeatures
        };
        if (config.required || config.advanced === false && !hide_optional_features) {
          acc.required_features.push(feature);
        } else {
          if (in_guided_connection && config.show_in_guided_connection || !in_guided_connection) {
            acc.advanced_features.push(feature);
          }
        }
        return acc;
      };
      $[5] = hide_optional_features;
      $[6] = in_guided_connection;
      $[7] = prefix;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = Object.entries(features).reduce(t4, default_features);
    $[0] = features;
    $[1] = hide_optional_features;
    $[2] = in_guided_connection;
    $[3] = prefix;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function calculateMargin(features: TypeFeatureFields, index: number, has_custom_auth = false) {
  const t = index < 2 && !has_custom_auth ? 0 : 3;
  return features.length === 1 ? {
    t
  } : {
    r: index % 2 === 0 ? 1.5 : 0,
    l: index % 2 !== 0 ? 1.5 : 0,
    t
  };
}
function VerificationFields(t0) {
  const $ = _c(5);
  const {
    verification_features,
    prefix,
    disabled,
    has_custom_auth
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== has_custom_auth || $[2] !== prefix || $[3] !== verification_features) {
    t1 = !!verification_features && verification_features.length > 0 ? <Div flex={{
      wrap: true
    }}>{verification_features.map((field, i) => {
        switch (field.type) {
          case "select":
            {
              return <GridUnit size={verification_features.length === 1 ? 1 : 0.5} key={field.name}><SelectInput disabled={disabled} m={calculateMargin(verification_features, i, has_custom_auth)} block={true} label={field.label} name={fieldName(field.name, prefix)} options={field.options ?? []} required={field.required === false ? false : true} /></GridUnit>;
            }
          case "textarea":
            {
              return <GridUnit size={1} key={field.name}><TextAreaInput disabled={disabled} m={calculateMargin(verification_features, i, has_custom_auth)} mono={true} label={field.label} placeholder={field.placeholder} default_value="" name={fieldName(field.name, prefix)} required={field.required === false ? false : true} /></GridUnit>;
            }
          default:
            {
              return <GridUnit size={verification_features.length === 1 ? 1 : 0.5} key={field.name}><TextInput disabled={disabled} m={calculateMargin(verification_features, i, has_custom_auth)} label={field.label} type={field.type} placeholder={field.placeholder} default_value="" name={fieldName(field.name, prefix)} required={field.required === false ? false : field.allow_empty ? false : true} /></GridUnit>;
            }
        }
      })}</Div> : null;
    $[0] = disabled;
    $[1] = has_custom_auth;
    $[2] = prefix;
    $[3] = verification_features;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function VerificationFeature(t0) {
  const $ = _c(102);
  const {
    feature,
    prefix
  } = t0;
  const {
    organization,
    source_types
  } = useContext(DashboardContext);
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("type", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2,, t3] = useField(t1);
  const {
    value: type
  } = t2;
  const {
    setValue: setType
  } = t3;
  let t4;
  if ($[2] !== prefix) {
    t4 = fieldName("config.auth_type", prefix);
    $[2] = prefix;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [t5,, t6] = useField(t4);
  const {
    value: auth_type
  } = t5;
  const {
    setTouched,
    setValue: setAuthType
  } = t6;
  let t7;
  if ($[4] !== prefix) {
    t7 = fieldName("enable_auth", prefix);
    $[4] = prefix;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const [t8] = useField(t7);
  const {
    value: enable_auth
  } = t8;
  let t9;
  if ($[6] !== prefix) {
    t9 = fieldName("config.auth", prefix);
    $[6] = prefix;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const [t10] = useField(t9);
  const {
    value: auth_values
  } = t10;
  const {
    setFieldValue
  } = useFormikContext();
  let t11;
  bb0: {
    let t12;
    if ($[8] !== feature) {
      t12 = isSourceTypeFeatureUnmanaged(feature) && feature.auth;
      $[8] = feature;
      $[9] = t12;
    } else {
      t12 = $[9];
    }
    const custom_auth = t12;
    if (!custom_auth) {
      let t13;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = [undefined, false];
        $[10] = t13;
      } else {
        t13 = $[10];
      }
      t11 = t13;
      break bb0;
    }
    const is_legacy_auth = enable_auth && type === "WEBHOOK" && auth_type && !custom_auth[auth_type];
    if (!is_legacy_auth) {
      let t13;
      if ($[11] !== custom_auth) {
        t13 = [custom_auth, false];
        $[11] = custom_auth;
        $[12] = t13;
      } else {
        t13 = $[12];
      }
      t11 = t13;
      break bb0;
    }
    const t13 = source_types?.[auth_type as SourceType]?.label;
    const t14 = source_types?.[auth_type as SourceType]?.features;
    const t15 = source_types?.[auth_type as SourceType]?.features.VERIFICATION as SourceTypeFeatureTypesUnmanaged;
    let t16;
    if ($[13] !== t13 || $[14] !== t14 || $[15] !== t15.fields) {
      t16 = {
        label: t13,
        features: t14,
        fields: t15.fields
      };
      $[13] = t13;
      $[14] = t14;
      $[15] = t15.fields;
      $[16] = t16;
    } else {
      t16 = $[16];
    }
    let t17;
    if ($[17] !== auth_type || $[18] !== custom_auth || $[19] !== t16) {
      t17 = [{
        ...custom_auth,
        [auth_type]: t16
      }, true];
      $[17] = auth_type;
      $[18] = custom_auth;
      $[19] = t16;
      $[20] = t17;
    } else {
      t17 = $[20];
    }
    t11 = t17;
  }
  const [custom_auth_0, is_legacy_auth_0] = t11;
  let t12;
  if ($[21] !== auth_type || $[22] !== custom_auth_0 || $[23] !== feature || $[24] !== type) {
    t12 = type && !!custom_auth_0 ? custom_auth_0[auth_type]?.fields : isSourceTypeFeatureUnmanaged(feature) && feature.fields ? feature.fields : undefined;
    $[21] = auth_type;
    $[22] = custom_auth_0;
    $[23] = feature;
    $[24] = type;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const verification_features = t12;
  const default_auth_type = isSourceTypeFeatureUnmanaged(feature) ? feature.default_auth_type : undefined;
  let t13;
  let t14;
  if ($[26] !== auth_type || $[27] !== custom_auth_0 || $[28] !== default_auth_type || $[29] !== enable_auth || $[30] !== setAuthType) {
    t13 = () => {
      if (!enable_auth || !custom_auth_0 || auth_type || !default_auth_type) {
        return;
      }
      setAuthType(default_auth_type);
    };
    t14 = [auth_type, custom_auth_0, default_auth_type, enable_auth, setAuthType];
    $[26] = auth_type;
    $[27] = custom_auth_0;
    $[28] = default_auth_type;
    $[29] = enable_auth;
    $[30] = setAuthType;
    $[31] = t13;
    $[32] = t14;
  } else {
    t13 = $[31];
    t14 = $[32];
  }
  useEffect(t13, t14);
  if (custom_auth_0) {
    let t15;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = {
        y: 4
      };
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    const t16 = !enable_auth;
    let t17;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = {
        y: 2,
        x: 3
      };
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = {
        justify: "flex-start",
        align: "center",
        gap: 4
      };
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let t19;
    if ($[36] !== prefix) {
      t19 = fieldName("enable_auth", prefix);
      $[36] = prefix;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let t20;
    if ($[38] !== t19) {
      t20 = <SwitchInput name={t19} />;
      $[38] = t19;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    const t21 = !enable_auth;
    let t22;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = {
        align: "center"
      };
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    let t23;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <Tooltip tooltip="Verify the authenticity of request(s) made to Hookdeck. A request that fails verification will be logged, but not delivered." cta={{
        label: "Learn more",
        to: LINKS.product_docs.sources_add_auth
      }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip>;
      $[41] = t23;
    } else {
      t23 = $[41];
    }
    let t24;
    if ($[42] !== t21) {
      t24 = <Text semi={true} muted={t21} flex={t22}>Authenticate{t23}</Text>;
      $[42] = t21;
      $[43] = t24;
    } else {
      t24 = $[43];
    }
    let t25;
    if ($[44] !== t20 || $[45] !== t24) {
      t25 = <StyledCardSection p={t17}><Div flex={t18} p={1}>{t20}{t24}</Div></StyledCardSection>;
      $[44] = t20;
      $[45] = t24;
      $[46] = t25;
    } else {
      t25 = $[46];
    }
    const t26 = !enable_auth;
    let t27;
    if ($[47] !== auth_type || $[48] !== is_legacy_auth_0 || $[49] !== setType || $[50] !== source_types) {
      t27 = is_legacy_auth_0 && <Div m={{
        x: 4,
        t: 4,
        b: 2
      }}><Alert inline={true} info={true}><Text>{source_types?.[auth_type as SourceType]?.label} authentication is now supported through it's dedicated source type. Change your source to{" "}{source_types?.[auth_type as SourceType]?.label} to update your authentication configuration.</Text><Div m={{
            t: 2
          }}><Link primary={true} onClick={() => {
              setType(auth_type as SourceType);
            }}>Migrate to {source_types?.[auth_type as SourceType]?.label} source type</Link></Div></Alert></Div>;
      $[47] = auth_type;
      $[48] = is_legacy_auth_0;
      $[49] = setType;
      $[50] = source_types;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    let t28;
    if ($[52] !== auth_values || $[53] !== custom_auth_0 || $[54] !== enable_auth || $[55] !== is_legacy_auth_0 || $[56] !== organization?.feature_flags?.oauth2_authorization_code || $[57] !== prefix || $[58] !== setFieldValue || $[59] !== setTouched || $[60] !== verification_features) {
      t28 = enable_auth && <StyledCard overflow_hidden={true} borderless={true}><StyledCardSection p={4}>{custom_auth_0 && <SelectInput m={0} block={true} required={true} label="Authentication Method" name={fieldName("config.auth_type", prefix)} onChange={next_auth_type => {
            setTouched(true);
            const next_fields = new Set((custom_auth_0[next_auth_type as SourceAuthProvider]?.fields || []).map(_temp));
            setFieldValue(fieldName("config.auth", prefix), Object.fromEntries(Object.entries(auth_values || {}).filter(t29 => {
              const [name] = t29;
              return next_fields.has(name);
            })));
          }} options={Object.entries(custom_auth_0).filter(t30 => {
            const [key] = t30;
            if (!organization?.feature_flags?.oauth2_authorization_code && key === "OAUTH2_AUTHORIZATION_CODE") {
              return false;
            }
            return true;
          }).map(_temp2)} />}<VerificationFields disabled={is_legacy_auth_0} verification_features={verification_features ?? []} prefix={fieldName("config.auth", prefix)} has_custom_auth={true} /></StyledCardSection></StyledCard>;
      $[52] = auth_values;
      $[53] = custom_auth_0;
      $[54] = enable_auth;
      $[55] = is_legacy_auth_0;
      $[56] = organization?.feature_flags?.oauth2_authorization_code;
      $[57] = prefix;
      $[58] = setFieldValue;
      $[59] = setTouched;
      $[60] = verification_features;
      $[61] = t28;
    } else {
      t28 = $[61];
    }
    let t29;
    if ($[62] !== t26 || $[63] !== t27 || $[64] !== t28) {
      t29 = <StyledCardSection borderless={t26}>{t27}{t28}</StyledCardSection>;
      $[62] = t26;
      $[63] = t27;
      $[64] = t28;
      $[65] = t29;
    } else {
      t29 = $[65];
    }
    let t30;
    if ($[66] !== t16 || $[67] !== t25 || $[68] !== t29) {
      t30 = <Div m={t15}><StyledCard overflow_hidden={true} dashed={t16}>{t25}{t29}</StyledCard></Div>;
      $[66] = t16;
      $[67] = t25;
      $[68] = t29;
      $[69] = t30;
    } else {
      t30 = $[69];
    }
    return t30;
  }
  if (isSourceTypeFeatureUnmanaged(feature) && feature.required) {
    let t15;
    if ($[70] !== verification_features) {
      t15 = verification_features ?? [];
      $[70] = verification_features;
      $[71] = t15;
    } else {
      t15 = $[71];
    }
    let t16;
    if ($[72] !== prefix) {
      t16 = fieldName("config.auth", prefix);
      $[72] = prefix;
      $[73] = t16;
    } else {
      t16 = $[73];
    }
    let t17;
    if ($[74] !== t15 || $[75] !== t16) {
      t17 = <VerificationFields verification_features={t15} prefix={t16} />;
      $[74] = t15;
      $[75] = t16;
      $[76] = t17;
    } else {
      t17 = $[76];
    }
    return t17;
  }
  let t15;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      y: 4
    };
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  const t16 = !enable_auth;
  let t17;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      y: 2,
      x: 3
    };
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  let t18;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      justify: "flex-start",
      align: "center",
      gap: 4
    };
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] !== prefix) {
    t19 = fieldName("enable_auth", prefix);
    $[80] = prefix;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== t19) {
    t20 = <SwitchInput name={t19} />;
    $[82] = t19;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  const t21 = !enable_auth;
  let t22;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      align: "center"
    };
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  let t23;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Tooltip tooltip="Enable authentication to verify the authenticity of the request made to Hookdeck. A request that fails verification will be logged, but not delivered." cta={{
      label: "Learn more",
      to: LINKS.product_docs.sources_add_auth
    }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip>;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== t21) {
    t24 = <Text semi={true} muted={t21} flex={t22}>Authenticate{t23}</Text>;
    $[86] = t21;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  let t25;
  if ($[88] !== t20 || $[89] !== t24) {
    t25 = <StyledCardSection p={t17}><Div flex={t18} p={1}>{t20}{t24}</Div></StyledCardSection>;
    $[88] = t20;
    $[89] = t24;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  const t26 = !enable_auth || !verification_features || verification_features.length === 0;
  let t27;
  if ($[91] !== enable_auth || $[92] !== prefix || $[93] !== verification_features) {
    t27 = enable_auth && verification_features && verification_features.length > 0 && <StyledCard overflow_hidden={true} borderless={true}><StyledCardSection p={4}><VerificationFields verification_features={verification_features} prefix={fieldName("config.auth", prefix)} /></StyledCardSection></StyledCard>;
    $[91] = enable_auth;
    $[92] = prefix;
    $[93] = verification_features;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== t26 || $[96] !== t27) {
    t28 = <StyledCardSection borderless={t26}>{t27}</StyledCardSection>;
    $[95] = t26;
    $[96] = t27;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] !== t16 || $[99] !== t25 || $[100] !== t28) {
    t29 = <Div m={t15}><StyledCard overflow_hidden={true} dashed={t16}>{t25}{t28}</StyledCard></Div>;
    $[98] = t16;
    $[99] = t25;
    $[100] = t28;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  return t29;
}
function _temp2(t0) {
  const [key_0, config] = t0;
  return {
    value: key_0,
    label: config.label
  };
}
function _temp(field) {
  return field.name;
}
function CustomHTTPFeature(t0) {
  const $ = _c(17);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.allowed_http_methods", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [, t2] = useField(t1);
  const {
    error
  } = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 4
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      l: 4,
      r: 3,
      y: 3
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      justify: "space-between",
      align: "center",
      gap: 3,
      wrap: true
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      align: "center"
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Text semi={true} flex={t6}>HTTP methods<Tooltip tooltip="Allow only specific HTTP methods for this source. Requests that don't match the one of the allowed methods will be rejected and logged." cta={{
        label: "Learn more",
        to: LINKS.product_docs.sources_custom_methods
      }}><Icon muted={true} right={true} pointer={true} icon="info" /></Tooltip></Text>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      gap: 3,
      align: "center",
      wrap: true
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== prefix) {
    t9 = source_method_display_order.map(method => <CheckboxInput key={method} m={0} label={method} name={fieldName(`config.allowed_http_methods.${method}`, prefix)} />);
    $[8] = prefix;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <StyledCard overflow_hidden={true}><StyledCardSection p={t4}><Div flex={t5}>{t7}<Div flex={t8}>{t9}</Div></Div></StyledCardSection></StyledCard>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== error) {
    t11 = error && <Text m={{
      t: 1,
      b: 0
    }} size="s" as="p" danger={true}><Icon icon="info" left={1} />{error}</Text>;
    $[12] = error;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11) {
    t12 = <Div m={t3}>{t10}{t11}</Div>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
export type CustomResponseValue = {
  content_type: 'json' | 'text' | 'xml';
  body: string;
};
function CustomResponseFeature(t0) {
  const $ = _c(41);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("enable_custom_response", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: enable_custom_response
  } = t2;
  let t3;
  if ($[2] !== prefix) {
    t3 = fieldName("config.custom_response", prefix);
    $[2] = prefix;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [t4] = useField(t3);
  const {
    value: custom_response
  } = t4;
  let t5;
  if ($[4] !== prefix) {
    t5 = fieldName("config.custom_response.content_type", prefix);
    $[4] = prefix;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [t6,, t7] = useField(t5);
  const {
    value: custom_response_content_type
  } = t6;
  const {
    setValue: setCustomResponseContentType
  } = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      y: 4
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = !enable_custom_response;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      justify: "space-between",
      align: "center"
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      gap: 4,
      align: "center"
    };
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== prefix) {
    t12 = fieldName("enable_custom_response", prefix);
    $[9] = prefix;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== t12) {
    t13 = <SwitchInput name={t12} />;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = !enable_custom_response;
  let t15;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      align: "center"
    };
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Tooltip tooltip="Override the default Hookdeck HTTP response with a custom static response in JSON, XML or TXT" cta={{
      label: "Learn more",
      to: LINKS.product_docs.sources_customize_response
    }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip>;
    $[14] = t16;
  } else {
    t16 = $[14];
  }
  let t17;
  if ($[15] !== t14) {
    t17 = <Text semi={true} muted={t14} flex={t15}>Customize response{t16}</Text>;
    $[15] = t14;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== t13 || $[18] !== t17) {
    t18 = <Div flex={t11} p={1}>{t13}{t17}</Div>;
    $[17] = t13;
    $[18] = t17;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      gap: 2,
      align: "center"
    };
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== custom_response_content_type || $[22] !== enable_custom_response || $[23] !== setCustomResponseContentType) {
    t20 = enable_custom_response && <DropdownMenu ghost={true} expand_icon="chevron_down" p={0} label={custom_response_content_type?.toUpperCase()} options={[{
      label: "JSON",
      onClick: () => setCustomResponseContentType("json")
    }, {
      label: "TEXT",
      onClick: () => setCustomResponseContentType("text")
    }, {
      label: "XML",
      onClick: () => setCustomResponseContentType("xml")
    }]} />;
    $[21] = custom_response_content_type;
    $[22] = enable_custom_response;
    $[23] = setCustomResponseContentType;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t20) {
    t21 = <Div flex={t19}>{t20}</Div>;
    $[25] = t20;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t18 || $[28] !== t21) {
    t22 = <StyledCardSection p={2}><Div flex={t10}>{t18}{t21}</Div></StyledCardSection>;
    $[27] = t18;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = !enable_custom_response;
  let t24;
  if ($[30] !== custom_response?.content_type || $[31] !== enable_custom_response || $[32] !== prefix) {
    t24 = enable_custom_response && <StyledCard overflow_hidden={true} borderless={true}><EditorInput name={fieldName("config.custom_response.body", prefix)} height="164px" language={custom_response?.content_type === "json" ? "json" : "text"} /></StyledCard>;
    $[30] = custom_response?.content_type;
    $[31] = enable_custom_response;
    $[32] = prefix;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== t23 || $[35] !== t24) {
    t25 = <StyledCardSection borderless={t23}>{t24}</StyledCardSection>;
    $[34] = t23;
    $[35] = t24;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== t22 || $[38] !== t25 || $[39] !== t9) {
    t26 = <Div m={t8}><StyledCard overflow_hidden={true} dashed={t9}>{t22}{t25}</StyledCard></Div>;
    $[37] = t22;
    $[38] = t25;
    $[39] = t9;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  return t26;
}
function ManagedTokenFeature(t0) {
  const $ = _c(6);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.auth.token", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: token
  } = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 4
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text size="xs" muted={true} semi={true} m={{
      b: 1
    }}>Source Token</Text>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== token) {
    t5 = <Div m={t3}>{t4}<CopyableField value={token} secret={true} /></Div>;
    $[4] = token;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function CustomUrlFeature(t0) {
  const $ = _c(15);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.url", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: url_value
  } = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 4
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== prefix) {
    t4 = fieldName("config.url", prefix);
    $[3] = prefix;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <TextInput name={t4} label="Destination URL" placeholder="https://example.com" help="The endpoint Hookdeck will direct HTTP requests to." required={true} mono={true} m={0} w={100} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== url_value) {
    t6 = url_value && url_value.includes("ngrok") && <Alert info={true} inline={true} m={{
      y: 4
    }}>Using Ngrok?{" "}<Link primary={true} href={LINKS.product_docs.cli} target="_blank" rel="noreferrer">Try our CLI{" "}</Link>with free permanent URLs</Alert>;
    $[7] = url_value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== url_value) {
    t7 = url_value && url_value.includes("localhost") && <Alert info={true} inline={true} m={{
      y: 4
    }}>Want to send events to a local server?{" "}<Link primary={true} href={LINKS.product_docs.cli} target="_blank" rel="noreferrer">Use our CLI {"->"}</Link></Alert>;
    $[9] = url_value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <Div m={t3}>{t5}{t6}{t7}</Div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function CustomCliPathFeature(t0) {
  const $ = _c(5);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      y: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== prefix) {
    t2 = fieldName("config.path", prefix);
    $[1] = prefix;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Div m={t1}><TextInput name={t2} label="CLI Path" placeholder="/webhooks" help="The HTTP endpoint path Hookdeck will append to your localhost." required={true} mono={true} m={0} w={100} /></Div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function CustomPathForwardingFeature(t0) {
  const $ = _c(19);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.path_forwarding", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: path_forwarding
  } = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 4
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !path_forwarding;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      justify: "space-between",
      align: "center"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: 4,
      align: "center"
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== prefix) {
    t7 = fieldName("config.path_forwarding", prefix);
    $[5] = prefix;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7) {
    t8 = <SwitchInput name={t7} />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = !path_forwarding;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Tooltip tooltip="Carry over the request path and append it to your destination path." cta={{
      label: "Learn more",
      to: LINKS.product_docs.destinations_advanced_edit
    }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t9) {
    t12 = <Text semi={true} muted={t9} flex={t10}>Path forwarding{t11}</Text>;
    $[11] = t9;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t12 || $[14] !== t8) {
    t13 = <StyledCardSection p={2}><Div flex={t5} p={1}><Div flex={t6}>{t8}{t12}</Div></Div></StyledCardSection>;
    $[13] = t12;
    $[14] = t8;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13 || $[17] !== t4) {
    t14 = <Div m={t3}><StyledCard overflow_hidden={true} dashed={t4}>{t13}</StyledCard></Div>;
    $[16] = t13;
    $[17] = t4;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  return t14;
}
function CustomDestinationHTTPMethodFeature(t0) {
  const $ = _c(12);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      y: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 4,
      y: 3
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      justify: "space-between",
      align: "center"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gap: 4,
      align: "center"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "center"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <StyledCardSection p={t2}><Div flex={t3}><Div flex={t4}><Text semi={true} flex={t5}>HTTP Method<Tooltip tooltip="Modify the requests to your destination to use a specific HTTP method. By default, the outbound event will be made with the same method as the inbound request." cta={{
              label: "Learn more",
              to: LINKS.product_docs.destinations_advanced_edit
            }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip></Text></Div></Div></StyledCardSection>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      x: 4,
      y: 4,
      t: 0
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== prefix) {
    t8 = fieldName("config.http_method", prefix);
    $[7] = prefix;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      value: "DEFAULT",
      label: "Matching request (Default)"
    }, ...destination_http_methods.map(_temp3)];
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t8) {
    t10 = <Div m={t1}><StyledCard overflow_hidden={true}>{t6}<StyledCardSection p={t7} borderless={true}><SelectInput block={true} m={0} name={t8} options={t9} /></StyledCardSection></StyledCard></Div>;
    $[10] = t8;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  return t10;
}
function _temp3(method) {
  return {
    value: method,
    label: method
  };
}
export function MaxThroughputRateFeature(t0) {
  const $ = _c(25);
  const {
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("enable_rate_limit", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: enabled_rate_limit
  } = t2;
  const {
    team
  } = useContext(DashboardContext);
  const t3 = !enabled_rate_limit;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      justify: "flex-start",
      align: "center",
      gap: 3
    };
    t5 = {
      x: 4,
      y: 3
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== prefix) {
    t6 = fieldName("enable_rate_limit", prefix);
    $[4] = prefix;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t6) {
    t7 = <SwitchInput name={t6} />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = !enabled_rate_limit;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "center"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Tooltip tooltip="Determine a maximum throughput rate for events en route to your destination. Events will be queued and delivered with the specified rate." cta={{
      label: "Learn more",
      to: LINKS.product_docs.destinations_max_delivery_rate
    }}><Icon muted={true} right={1.5} small={true} pointer={true} icon="info" /></Tooltip>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t8) {
    t11 = <Text semi={true} muted={t8} flex={t9}>Max delivery rate{t10}</Text>;
    $[10] = t8;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11 || $[13] !== t7) {
    t12 = <Div flex={t4} p={t5}>{t7}{t11}</Div>;
    $[12] = t11;
    $[13] = t7;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== enabled_rate_limit || $[16] !== prefix || $[17] !== team?.max_events_per_second) {
    t13 = enabled_rate_limit && <Div p={{
      x: 4,
      b: 4
    }}><Grid><GridUnit size={0.5}><TextInput m={{
            r: 2,
            b: 0
          }} name={fieldName("config.rate_limit", prefix)} default_value={team?.max_events_per_second ? team?.max_events_per_second.toString() : "5"} type="number" min={1} required={true} /></GridUnit><GridUnit size={0.5}><SelectInput m={{
            l: 2,
            b: 0
          }} block={true} name={fieldName("config.rate_limit_period", prefix)} options={[{
            value: "second",
            label: "per second"
          }, {
            value: "minute",
            label: "per minute"
          }, {
            value: "hour",
            label: "per hour"
          }, {
            value: "concurrent",
            label: "concurrent"
          }]} required={true} /></GridUnit></Grid></Div>;
    $[15] = enabled_rate_limit;
    $[16] = prefix;
    $[17] = team?.max_events_per_second;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <StyledCardSection>{t12}{t13}</StyledCardSection>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t3) {
    t15 = <StyledCard overflow_hidden={true} dashed={t3}>{t14}</StyledCard>;
    $[22] = t14;
    $[23] = t3;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
}
function DestinationVerificationFeature(t0) {
  const $ = _c(42);
  const {
    feature,
    prefix
  } = t0;
  const {
    organization
  } = useContext(DashboardContext);
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.auth_type", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2,, t3] = useField(t1);
  const {
    value: auth_type
  } = t2;
  const {
    setTouched
  } = t3;
  const custom_auth = !feature.managed && "auth" in feature ? feature.auth : undefined;
  const verification_features = custom_auth ? custom_auth[auth_type]?.fields : undefined;
  if (custom_auth) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        y: 4
      };
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        x: 4,
        y: 3
      };
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        align: "center"
      };
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    let t7;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <StyledCardSection p={t5}><Text semi={true} flex={t6}>Authentication method<Tooltip tooltip="Determine a specific authentication method for your destination. By default, a Hookdeck-generated signature is used. Hookdeck will append the necessary headers or query parameters to the request for every authentication type." cta={{
            label: "Learn more",
            to: LINKS.product_docs.destinations_auth_strategy
          }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip></Text></StyledCardSection>;
      $[5] = t7;
    } else {
      t7 = $[5];
    }
    let t8;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        x: 4,
        y: 4,
        t: 0
      };
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    let t9;
    if ($[7] !== custom_auth || $[8] !== organization?.feature_flags?.oauth2_authorization_code || $[9] !== prefix || $[10] !== setTouched) {
      t9 = custom_auth && <SelectInput m={0} block={true} name={fieldName("config.auth_type", prefix)} onChange={() => setTouched(true)} options={Object.entries(custom_auth).filter(t10 => {
        const [key] = t10;
        if (!organization?.feature_flags?.oauth2_authorization_code && key === "OAUTH2_AUTHORIZATION_CODE") {
          return false;
        }
        return true;
      }).map(_temp4)} />;
      $[7] = custom_auth;
      $[8] = organization?.feature_flags?.oauth2_authorization_code;
      $[9] = prefix;
      $[10] = setTouched;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] !== prefix) {
      t10 = fieldName("config.auth", prefix);
      $[12] = prefix;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10 || $[15] !== verification_features) {
      t11 = <VerificationFields verification_features={verification_features} prefix={t10} has_custom_auth={true} />;
      $[14] = t10;
      $[15] = verification_features;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== t11 || $[18] !== t9) {
      t12 = <Div m={t4}><StyledCard overflow_hidden={true}>{t7}<StyledCardSection p={t8} borderless={true}>{t9}{t11}</StyledCardSection></StyledCard></Div>;
      $[17] = t11;
      $[18] = t9;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    return t12;
  }
  if (!feature.managed && "required" in feature && feature.required) {
    let t4;
    if ($[20] !== prefix) {
      t4 = fieldName("config.auth", prefix);
      $[20] = prefix;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    let t5;
    if ($[22] !== t4 || $[23] !== verification_features) {
      t5 = <VerificationFields verification_features={verification_features} prefix={t4} />;
      $[22] = t4;
      $[23] = verification_features;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    return t5;
  }
  let t4;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      y: 4
    };
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      y: 2,
      x: 3
    };
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      justify: "flex-start",
      align: "center",
      gap: 4
    };
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== prefix) {
    t7 = fieldName("enable_auth", prefix);
    $[28] = prefix;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t7) {
    t8 = <SwitchInput name={t7} />;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "center"
    };
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text semi={true} flex={t9}>Authenticate<Tooltip tooltip="Enable authentication to verify the authenticity of the request made to Hookdeck. A request that fails verification will be logged, but not delivered." cta={{
        label: "Learn more",
        to: LINKS.product_docs.sources_add_auth
      }}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip></Text>;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t8) {
    t11 = <StyledCardSection p={t5}><Div flex={t6} p={1}>{t8}{t10}</Div></StyledCardSection>;
    $[34] = t8;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== prefix || $[37] !== verification_features) {
    t12 = verification_features && verification_features.length > 0 && <StyledCardSection p={4}><VerificationFields verification_features={verification_features} prefix={fieldName("config.auth", prefix)} /></StyledCardSection>;
    $[36] = prefix;
    $[37] = verification_features;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11 || $[40] !== t12) {
    t13 = <Div m={t4}><StyledCard overflow_hidden={true}>{t11}{t12}</StyledCard></Div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}
function _temp4(t0) {
  const [key_0, config] = t0;
  return {
    value: key_0,
    label: config.label
  };
}
function TrimPayloadFeature(t0) {
  const $ = _c(23);
  const {
    feature,
    prefix
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = fieldName("config.trim_payload", prefix);
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useField(t1);
  const {
    value: trim_payload
  } = t2;
  const field = isSourceTypeFeatureUnmanaged(feature) ? feature.fields?.[0] : undefined;
  if (!field) {
    return null;
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 4
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !trim_payload;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      justify: "space-between",
      align: "center"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: 4,
      align: "center"
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = `config.${field.name}`;
  let t8;
  if ($[5] !== prefix || $[6] !== t7) {
    t8 = fieldName(t7, prefix);
    $[5] = prefix;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t8) {
    t9 = <SwitchInput name={t8} />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = !trim_payload;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      align: "center"
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== field.description) {
    t12 = field.description && <Tooltip tooltip={field.description}><Icon muted={true} right={true} small={true} pointer={true} icon="info" /></Tooltip>;
    $[11] = field.description;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== field.label || $[14] !== t10 || $[15] !== t12) {
    t13 = <Text semi={true} muted={t10} flex={t11}>{field.label}{t12}</Text>;
    $[13] = field.label;
    $[14] = t10;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t13 || $[18] !== t9) {
    t14 = <StyledCardSection p={2}><Div flex={t5} p={1}><Div flex={t6}>{t9}{t13}</Div></Div></StyledCardSection>;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t14 || $[21] !== t4) {
    t15 = <Div m={t3}><StyledCard overflow_hidden={true} dashed={t4}>{t14}</StyledCard></Div>;
    $[20] = t14;
    $[21] = t4;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  return t15;
}
export type SourceFeatureType = SourceTypeFeatures['VERIFICATION'] & {
  type: keyof SourceTypeFeatures | keyof DestinationTypeFeatures;
};
export function SourceFeature(t0) {
  const $ = _c(15);
  const {
    feature,
    prefix
  } = t0;
  switch (feature.type) {
    case "VERIFICATION":
      {
        let t1;
        if ($[0] !== feature || $[1] !== prefix) {
          t1 = <VerificationFeature feature={feature} prefix={prefix} />;
          $[0] = feature;
          $[1] = prefix;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "CUSTOM_HTTP_METHOD":
      {
        let t1;
        if ($[3] !== feature || $[4] !== prefix) {
          t1 = <CustomHTTPFeature feature={feature} prefix={prefix} />;
          $[3] = feature;
          $[4] = prefix;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "CUSTOM_RESPONSE":
      {
        let t1;
        if ($[6] !== feature || $[7] !== prefix) {
          t1 = <CustomResponseFeature feature={feature} prefix={prefix} />;
          $[6] = feature;
          $[7] = prefix;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    case "MANAGED_TOKEN":
      {
        let t1;
        if ($[9] !== feature || $[10] !== prefix) {
          t1 = <ManagedTokenFeature feature={feature} prefix={prefix} />;
          $[9] = feature;
          $[10] = prefix;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "TRIM_PAYLOAD":
      {
        let t1;
        if ($[12] !== feature || $[13] !== prefix) {
          t1 = <TrimPayloadFeature feature={feature} prefix={prefix} />;
          $[12] = feature;
          $[13] = prefix;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
}
export type DestinationFeatureType = DestinationTypeFeatures['VERIFICATION'] & {
  type: keyof DestinationTypeFeatures;
};
export function DestinationFeature(t0) {
  const $ = _c(15);
  const {
    feature,
    prefix
  } = t0;
  switch (feature.type) {
    case "VERIFICATION":
      {
        let t1;
        if ($[0] !== feature || $[1] !== prefix) {
          t1 = <DestinationVerificationFeature feature={feature} prefix={prefix} />;
          $[0] = feature;
          $[1] = prefix;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "CUSTOM_URL":
      {
        let t1;
        if ($[3] !== feature || $[4] !== prefix) {
          t1 = <CustomUrlFeature feature={feature} prefix={prefix} />;
          $[3] = feature;
          $[4] = prefix;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "CUSTOM_CLI_PATH":
      {
        let t1;
        if ($[6] !== feature || $[7] !== prefix) {
          t1 = <CustomCliPathFeature feature={feature} prefix={prefix} />;
          $[6] = feature;
          $[7] = prefix;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    case "PATH_FORWARDING":
      {
        let t1;
        if ($[9] !== feature || $[10] !== prefix) {
          t1 = <CustomPathForwardingFeature feature={feature} prefix={prefix} />;
          $[9] = feature;
          $[10] = prefix;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "CUSTOM_DESTINATION_HTTP_METHOD":
      {
        let t1;
        if ($[12] !== feature || $[13] !== prefix) {
          t1 = <CustomDestinationHTTPMethodFeature feature={feature} prefix={prefix} />;
          $[12] = feature;
          $[13] = prefix;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
}